import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  IconButton,
  Pagination,
  Typography,
  Badge,
  Tooltip,
  Button,
} from "@mui/material";

import CommentIcon from "@mui/icons-material/Comment";
import {
  bgcolors,
  colors,
  getNextPath,
  getParentPath,
  Node,
  NodeType,
} from "./Utils";
import { ExpandMore, ChevronRight, Info, Edit } from "@mui/icons-material";
import {
  columnCenter,
  displayColumn,
  displayRow,
  justifEnd,
  rowCenter,
  rowHorizontalCenter,
  rowHorizontalEnd,
} from "../../GlobalStyles";
import CountChips from "../../Components/AllCards/layouts/CountChips";
import Actions from "./Actions";
import CustomPopover from "../../Components/CustomPopover";
import Goal from "../../Components/AllCards/Goal";
import Section from "../../Components/AllCards/Section";
import Unit from "../../Components/AllCards/Unit";
import Topic from "../../Components/AllCards/Topic";
import Subtopic from "../../Components/AllCards/Subtopic";
import Cluster from "../../Components/AllCards/Cluster";
import CardFooter from "../../Components/AllCards/layouts/CardFooter";
import { chipConfigs } from "./Utils";
import ExamCard from "../../Components/AllCards/ExamCard";
import HoverCard from "./HoverCard";
import { convertFirstUpper } from "../../store/slices/constants/staticContents";
import { edit, eyeOpen } from "../../assets/svgs";
import { useRef, useState } from "react";
import { singleLineEllipsis } from "../../Components/AllCards/layouts/CardHeader";
import GlobalLoader from "../../Components/GlobalLoader";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { RootState } from "../../store/store";
import CustomProgress from "../../Components/CustomProgress";
import { ObjectValues } from "../login";
import BulkQuestionsForm from "../exams/BulkQuestionsForm";
import UpdatedQuestionPreviewWrapper from "../contentFeedback/UpdatedQuestionPreviewWrapper";
import ReportedQuestionPreview from "../contentFeedback/ReportedQuestionPreview";
import MessagePopper from "../contentFeedback/MessagePopper";
import globalColors from "../../globalColors";
import ImportQuestionsFromMock from "./ImportQuestionsFromMock";
import { showSnack } from "../../store/slices/constants";
import apiRequest from "../../request/apiRequest/apiRequest";

const ROOT_KEY = "__ROOT__";
const parentKey = (parentId: string | null) => parentId ?? ROOT_KEY;

export default function NodeItem({
  node,
  depth,
  parentId,
  selectedItems,
  onToggleSelect,
  expandedMap,
  onToggleExpand,
  headerDetail,
  cut,
  getFilteredList,
  onlyPreview,
  selectAll,
  fullscreen,
  setfullscreen,
  handleDeleteSingle,
  handleStatusSingle,
  hierarchy,
  setUpdate,
  update,
  onReFetch,
  setPagination,
  pagination,
  setLoader,
  onCut,
  plist,
  onEdit,
  setNotesOpen,
}: {
  node: Node;
  depth: number;
  parentId: string | null;
  selectedItems: any;
  onToggleSelect: (id: string, level: NodeType) => void;
  expandedMap: Record<NodeType, string | null>;
  onToggleExpand: (type: NodeType, nodeId: string, parent: string) => void;
  headerDetail: (data: any) => any;
  cut: any;
  getFilteredList: (list: Node[], type: NodeType) => any;
  onlyPreview: any;
  selectAll: boolean;
  fullscreen: any;
  setfullscreen: any;
  handleDeleteSingle: any;
  handleStatusSingle: any;
  hierarchy: any;
  setUpdate: any;
  update: any;
  onReFetch: (type: NodeType) => void;
  setPagination: React.Dispatch<React.SetStateAction<Record<number, any>>>;
  pagination: Record<NodeType, number | null>;
  setLoader: any;
  onCut: any;
  plist: Node[];
  onEdit?: any;
  setNotesOpen?: any;
}) {
  const rowPerPage = 5;
  const [questionPreviewOpen, setQuestionPreviewOpen] = useState<false | Node>(
    false
  );
  const dispatch = useAppDispatch();
  const dialogRef = useRef({});
  const [isBulkQuestionDialogOpen, setIsBulkQuestionDialogOpen] = useState<{
    open: boolean;
    data?: ObjectValues;
    type?: "pyq";
  }>({ open: false });
  const handleBulkQuestionsSubmit = (questions: any) => {
    onReFetch("QUESTION");
    setIsBulkQuestionDialogOpen({ open: false });
  };

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const isSelected = selectedItems[node.type]
    ? (selectedItems[node.type] ?? []).includes(node.id)
    : node.type === "EXAM"
    ? false
    : selectAll
    ? true
    : false;
  const { masters, loading } = useAppSelecter(
    (state: RootState) => state.detailedMaster
  );

  const totalPages = Math.ceil(
    (
      getFilteredList(node?.children ?? [], getNextPath(node.type)) as Node[]
    ).filter((s) =>
      onlyPreview ? (selectedItems[s.type] ?? []).includes(s.id) : true
    ).length / rowPerPage
  );

  const currentPage =
    (pagination[getNextPath(node.type)] ?? 1) >
    Math.ceil(
      (
        getFilteredList(node?.children ?? [], getNextPath(node.type)) as Node[]
      ).filter((s) =>
        onlyPreview ? (selectedItems[s.type] ?? []).includes(s.id) : true
      ).length / rowPerPage
    )
      ? 1
      : pagination[getNextPath(node.type)] ?? 1;

  const myParentKey = parentKey(parentId);
  const isExpanded = expandedMap[node.type] === node.id;
  const details = node.details;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleExpand(node.type, node.id, myParentKey);
  };

  const getPaginated = (list: Node[]): Node[] => {
    return list.slice((currentPage - 1) * rowPerPage, currentPage * rowPerPage);
  };

  const isFullscreen = fullscreen === `parent-${node.type}-${node.id}`;

  const child = (
    <>
      {isBulkQuestionDialogOpen.open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "background.paper",
            zIndex: 1300,
          }}
        >
          {isBulkQuestionDialogOpen.type === "pyq" ? (
            <ImportQuestionsFromMock
              setLoader={setLoader}
              hierarchy={{
                ...hierarchy,
                [node.type.toLowerCase()]: node.label,
                [`${node.type.toLowerCase()}_id`]: node.id,
              }}
              topic={isBulkQuestionDialogOpen.data}
              dialogRef={dialogRef}
              onSubmit={handleBulkQuestionsSubmit}
              onClose={() => setIsBulkQuestionDialogOpen({ open: false })}
            />
          ) : (
            <BulkQuestionsForm
              hierarchy={{
                ...hierarchy,
                [node.type.toLowerCase()]: node.label,
                [`${node.type.toLowerCase()}_id`]: node.id,
              }}
              subtopic={isBulkQuestionDialogOpen.data}
              dialogRef={dialogRef}
              onSubmit={handleBulkQuestionsSubmit}
              onClose={() => setIsBulkQuestionDialogOpen({ open: false })}
            />
          )}
        </Box>
      )}

      <Box
        id={`${node.type}-${node.id}`}
        display="flex"
        alignItems="center"
        sx={{
          px: 1.5,
          py: 1,
          background: isExpanded
            ? bgcolors[node.type]
              ? bgcolors[node.type]
              : ""
            : "",
          borderLeft: (theme) =>
            `4px solid ${
              isExpanded
                ? colors[node.type]
                  ? colors[node.type]
                  : theme.palette.primary.light
                : "transparent"
            }`,
          opacity: cut && isSelected ? 0.4 : 1,
          position: "relative", // important for badge positioning
          ...displayColumn,
        }}
        onContextMenu={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchorEl({
            e: e.currentTarget,
            node: node,
            axis: { mouseX: e.clientX - 2, mouseY: e.clientY - 4 },
          });
        }}
      >
        <Box sx={{ ...displayRow, width: "100%" }}>
          {!isFullscreen && node.type !== "QUESTION" && (
            <IconButton
              size="small"
              onClick={toggleExpand}
              aria-label={isExpanded ? "collapse" : "expand"}
            >
              {isExpanded ? (
                <ExpandMore fontSize="small" />
              ) : (
                <ChevronRight fontSize="small" />
              )}
            </IconButton>
          )}

          {!isFullscreen && (
            <Checkbox
              size="small"
              checked={
                (selectedItems[getNextPath(node.type)]
                  ? isSelected
                  : (selectedItems[node.type] ?? []).includes(node.id)
                  ? true
                  : false) || isSelected
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggleSelect(node.id, node.type);
              }}
              sx={{ ml: 0.5 }}
            />
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600 }}
              onClick={() => {
                navigator.clipboard.writeText(node.label);
                dispatch(
                  showSnack({
                    open: true,
                    severity: "success",
                    message: `Copied.`,
                  })
                );
              }}
            >
              {node.type === "QUESTION" ? (
                <div
                  style={{
                    minHeight: "49.54px",
                    maxWidth: "400px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuestionPreviewOpen(node);
                  }}
                >
                  <div style={{ height: "49.54px" }}>
                    {/* <style>{`img { max-width: 100%; max-height: 25px; }`}</style> */}
                    <MessagePopper
                      text={node.details.text ? node.details.text : ""}
                      type="ck"
                      id={"question"}
                    />
                  </div>
                </div>
              ) : isFullscreen ? (
                (() => {
                  const hierarchyOrder = [
                    "exam",
                    "goal",
                    "section",
                    "unit",
                    "topic",
                    "subtopic",
                    "cluster",
                  ];
                  let parts: string[] = [];
                  for (const key of hierarchyOrder) {
                    if (hierarchy[key]) {
                      parts.push(hierarchy[key]);
                    } else {
                      parts.push(node.label);
                      break;
                    }
                  }
                  if (
                    parts.length === hierarchyOrder.length &&
                    parts[parts.length - 1] !== node.label
                  ) {
                    parts.push(node.label);
                  }
                  return parts.join(" • ");
                })()
              ) : (
                node.label
              )}
            </Typography>

            <Chip
              label={`id: #${node.id}`}
              size="small"
              sx={{
                border: "none",
                borderRadius: "4px",
                padding: "0",
                color: "rgb(0 0 0 / 60%)",
                fontSize: "0.75rem",
                background: "transparent",
                cursor: "text",
                "& .MuiChip-label": {
                  padding: "0 3px !important",
                },
                ...singleLineEllipsis,
                ":hover": {
                  background: "transparent",
                },
                ":focus": {
                  background: "transparent",
                },
              }}
              onClick={() => {
                navigator.clipboard.writeText(node.id);
                dispatch(
                  showSnack({
                    open: true,
                    severity: "success",
                    message: `Id #${node.id} Copied.`,
                  })
                );
              }}
            />

            {node.status && (
              <Box
                sx={{
                  borderRadius: "8px",
                  padding: "6px 10px",
                  background:
                    globalColors.colorCombinations[node.status]?.bg ?? "",
                  color:
                    globalColors.colorCombinations[node.status]?.color ?? "",
                  textTransform: "capitalize",
                  ...rowHorizontalCenter,
                  maxWidth: "100%",
                  fontSize: "0.75rem",
                  lineHeight: "normal",
                  textOverflow: "ellipsis",
                  whiteSpace: "normal",
                  overflow: "hidden",
                  width: "fit-content",
                }}
              >
                {convertFirstUpper(node.status)}
              </Box>
            )}
            {/* {node.type === "QUESTION" && ( */}
            <Box
              sx={{ cursor: "pointer", p: "2px", ...columnCenter, ml: "15px" }}
              onClick={() => {
                setUpdate({
                  node: node,
                  hierarchy: hierarchy,
                  parent: parentId,
                  plist: plist,
                });
              }}
            >
              {edit}
            </Box>
            {/* )} */}

            {/* Badge for total_mocktest_questions */}
            {(node.details.total_mocktest_questions > 0 ||
              node.label.includes("cluster_mocktest")) && (
              <Tooltip
                title={`${
                  node.label.includes("cluster_mocktest")
                    ? node.details.total_questions
                    : node.details.total_mocktest_questions
                } question${
                  node.label.includes("cluster_mocktest")
                    ? node.details.total_questions
                    : node.details.total_mocktest_questions > 1
                    ? "s"
                    : ""
                } are remaining to be reviewed`}
                arrow
                placement="top"
              >
                <Badge
                  badgeContent={
                    node.label.includes("cluster_mocktest")
                      ? node.details.total_questions
                      : node.details.total_mocktest_questions
                  }
                  color="error"
                  sx={{
                    ml: 2,
                    "& .MuiBadge-badge": {
                      top: -10,
                      right: -10,
                      fontSize: "0.75rem",
                    },
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}

            {/* Badge for total_cluster_recommended_questions */}
            {node.details.total_cluster_recommended_questions > 0 && (
              <Tooltip
                title={`${node.details.total_cluster_recommended_questions} question${
                  node.details.total_cluster_recommended_questions > 1
                    ? "s"
                    : ""
                } remaining for cluster verification`}
                arrow
                placement="top"
              >
                <Badge
                  badgeContent={node.details.total_cluster_recommended_questions}
                  color="success"
                  sx={{
                    ml: 4,
                    "& .MuiBadge-badge": {
                      top: -10,
                      right: -10,
                      fontSize: "0.75rem",
                    },
                    cursor: "pointer",
                  }}
                />
              </Tooltip>
            )}

            {/* Badge for recommended_cluster_name */}
            {node.details.recommended_cluster_name && (
              <>
                <Tooltip
                  title={`Recommended cluster (second): ${node.details.recommended_cluster_name}`}
                  arrow
                  placement="top"
                >
                  <Chip
                    label={node.details.recommended_cluster_name}
                    size="small"
                    color="info"
                    sx={{
                      ml: 2,
                      maxWidth: "150px",
                      fontSize: "0.75rem",
                      height: "24px",
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      setLoader(true);
                      const response = await apiRequest({
                        api: `cms/content/cluster/unrecommend/${node.id}`,
                        method: "POST",
                      });
                      if (response && !response.error) {
                        dispatch(
                          showSnack({
                            open: true,
                            severity: "success",
                            message: response?.detail || "Question unrecommended successfully",
                          })
                        );
                        // Refresh both QUESTION and CLUSTER data to update counts
                        onReFetch("QUESTION");
                        onReFetch("CLUSTER");
                      } else {
                        dispatch(
                          showSnack({
                            open: true,
                            severity: "error",
                            message: response?.detail || response?.msg || "Failed to unrecommend question",
                          })
                        );
                      }
                    } catch (error: any) {
                      dispatch(
                        showSnack({
                          open: true,
                          severity: "error",
                          message: error.message || "An error occurred",
                        })
                      );
                    } finally {
                      setLoader(false);
                    }
                  }}
                  sx={{
                    ml: 1.5,
                    fontSize: "0.75rem",
                    height: "24px",
                    minWidth: "auto",
                    px: 1.5,
                    textTransform: "none",
                  }}
                >
                  Finalise
                </Button>
              </>
            )}
          </Box>

          <Box
            sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box sx={{ gap: "8px", ...displayRow, ...justifEnd }}>
              {chipConfigs(details).map((chip: any) => (
                <Box
                  key={chip.key}
                  sx={{ maxWidth: "102px !important", width: "auto" }}
                >
                  <CountChips
                    label={chip.label}
                    value={chip.value}
                    color={chip.color}
                    text={chip.text}
                  />
                </Box>
              ))}

              {questionPreviewOpen !== false &&
                questionPreviewOpen.id === details.id &&
                questionPreviewOpen.type === node.type && (
                  <ReportedQuestionPreview
                    handleDialogClose={() => {
                      setQuestionPreviewOpen(false);
                    }}
                    title={(() => {
                      const hierarchyOrder = [
                        "exam",
                        "goal",
                        "section",
                        "unit",
                        "topic",
                        "subtopic",
                        "cluster",
                      ];
                      let parts: string[] = [];
                      for (const key of hierarchyOrder) {
                        if (hierarchy[key]) {
                          parts.push(hierarchy[key]);
                        } else {
                          parts.push(node.label);
                          break;
                        }
                      }
                      if (
                        parts.length === hierarchyOrder.length &&
                        parts[parts.length - 1] !== node.label
                      ) {
                        parts.push(node.label);
                      }
                      return parts.join(" • ");
                    })()}
                    selectedRow={{ question: details.id }}
                  />
                )}

              <Box
                sx={{
                  maxWidth: "fit-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <HoverCard
                  icon={
                    <Info
                      sx={{ color: "#9ca3af", height: "16px", width: "16px" }}
                    />
                  }
                  content={
                    node.type === "EXAM" ? (
                      <ExamCard data={details} />
                    ) : node.type === "GOAL" ? (
                      <Goal data={details} />
                    ) : node.type === "SECTION" ? (
                      <Section data={details} />
                    ) : node.type === "UNIT" ? (
                      <Unit data={details} />
                    ) : node.type === "TOPIC" ? (
                      <Topic data={details} />
                    ) : node.type === "SUBTOPIC" ? (
                      <Subtopic data={details} />
                    ) : node.type === "CLUSTER" ? (
                      <Cluster data={details} />
                    ) : (
                      <CardFooter
                        internal_remark={details.internal_remark}
                        created_at={details.created_at}
                        created_by_id={details.created_by_id}
                        modified_at={details.modified_at}
                        modified_by_id={details.modified_by_id}
                      />
                    )
                  }
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex" }}>
              <Box sx={{ cursor: "pointer", ...columnCenter }}>
                <IconButton
                  onClick={() => {
                    setNotesOpen({
                      node: node,
                      hierarchy: hierarchy,
                      parent: parentId,
                      plist: plist,
                    });
                  }}
                  sx={{
                    p: "4px",
                    borderRadius: "6px",
                    "&:hover": { backgroundColor: "rgba(25,118,210,0.08)" },
                  }}
                >
                  <CommentIcon
                    sx={{ color: "#9ca3af", height: "16px", width: "16px" }}
                  />
                </IconButton>
              </Box>
              {!onlyPreview && (
                <Actions
                  hierarchy={{
                    ...hierarchy,
                    [node.type.toLowerCase()]: node.label,
                    [`${node.type.toLowerCase()}_id`]: node.id,
                    [`parent_id_stc${node.type.toLowerCase()}`]:
                      node.type === "SUBTOPIC" ? node.details.parent_id : "",
                  }}
                  bulkaddQuestion={
                    node && node.type === "SUBTOPIC"
                      ? () => {
                          setIsBulkQuestionDialogOpen({
                            data: node.details,
                            open: true,
                          });
                        }
                      : undefined
                  }
                  bulkcopyQuestion={
                    node && node.type === "TOPIC"
                      ? () => {
                          setIsBulkQuestionDialogOpen({
                            data: node.details,
                            open: true,
                            type: "pyq",
                          });
                        }
                      : undefined
                  }
                  anchorEls={anchorEl}
                  setAnchorEls={setAnchorEl}
                  type={node ? node.type : "EXAM"}
                  onCut={() => {
                    onCut({
                      type: getParentPath(node.type),
                      id: parentId,
                      label: hierarchy[getParentPath(node.type).toLowerCase()],
                      children: [node],
                    });
                  }}
                  onDelete={() =>
                    handleDeleteSingle(
                      node,
                      node.type === "CLUSTER" ? parentId : null
                    )
                  }
                  onStatus={(status: any) => handleStatusSingle(node, status)}
                  disabled={false}
                  row={details}
                  setLoader={setLoader}
                  onReFetch={onReFetch}
                />
              )}
            </Box>
          </Box>
        </Box>
        {details.note_text && (
          <Box sx={{ ...rowHorizontalEnd, width: "100%",borderTop:`1px solid #e5e7eb`,mt:"8px",pt:"8px" }}>
            <Typography variant="body2"m="0">
              <em>Note:</em> <strong> {details.note_text ?? ""}</strong>
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={
          isFullscreen
            ? {
                flex: 8,
                ...displayColumn,
                background: "#F4F5FA",
                height: "calc(100dvh - 66px)",
              }
            : { background: "#F4F5FA" }
        }
      >
        <Collapse
          in={isExpanded}
          timeout="auto"
          unmountOnExit
          sx={
            isFullscreen
              ? { height: "100%", "& .MuiCollapse-wrapper": { height: "100%" } }
              : {}
          }
        >
          <Box sx={isFullscreen ? { height: "100%", ...displayColumn } : {}}>
            {headerDetail({
              node,
              parentId,
              onToggleExpand,
              isFullscreen,
              plist: plist,
            })}

            {(node.type === "CLUSTER" && loading.questions) ||
            loading[`${getNextPath(node.type)}s` as keyof typeof loading] ||
            loading[
              `${
                node?.children
                  ? getNextPath(node.type).toLowerCase()
                  : node?.type.toLowerCase()
              }s` as keyof typeof loading
            ] ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100px",
                  minHeight: "100px",
                  ...columnCenter,
                }}
              >
                <CustomProgress
                  sx={{
                    height: "50px !important",
                    width: "50px !important",
                    color: "black !important",
                  }}
                />
              </Box>
            ) : (
              ""
            )}

            {node.children && node.children.length > 0 && (
              <Box
                className="scrollBluePrimarythin"
                sx={
                  isFullscreen
                    ? { width: "100%", height: "100%", overflowY: "auto" }
                    : {}
                }
              >
                {(
                  getFilteredList(
                    node.children,
                    getNextPath(node.type)
                  ) as Node[]
                ).filter((s) =>
                  onlyPreview
                    ? (selectedItems[s.type] ?? []).includes(s.id)
                    : true
                ).length > 0 ? (
                  getPaginated(
                    (
                      getFilteredList(
                        node.children,
                        getNextPath(node.type)
                      ) as Node[]
                    ).filter((s) =>
                      onlyPreview
                        ? (selectedItems[s.type] ?? []).includes(s.id)
                        : true
                    )
                  ).map((child: Node) => (
                    <NodeItem
                      onEdit={onEdit}
                      setNotesOpen={setNotesOpen}
                      plist={node.children ?? []}
                      onReFetch={onReFetch}
                      onCut={onCut}
                      setLoader={setLoader}
                      setPagination={setPagination}
                      pagination={pagination}
                      hierarchy={{
                        ...hierarchy,
                        [node.type.toLowerCase()]: node.label,
                        [`${node.type.toLowerCase()}_id`]: node.id,
                        [`parent_id_stc${node.type.toLowerCase()}`]:
                          node.type === "SUBTOPIC"
                            ? node.details.parent_id
                            : "",
                      }}
                      setUpdate={setUpdate}
                      update={update}
                      handleDeleteSingle={handleDeleteSingle}
                      handleStatusSingle={handleStatusSingle}
                      getFilteredList={getFilteredList}
                      key={child.id}
                      fullscreen={fullscreen}
                      setfullscreen={setfullscreen}
                      node={child}
                      depth={depth + 1}
                      parentId={node.id}
                      selectAll={
                        selectedItems[child.type]
                          ? false
                          : (selectedItems[node.type] ?? []).includes(
                              node.id
                            ) || selectAll
                          ? true
                          : false
                      }
                      onlyPreview={onlyPreview}
                      selectedItems={selectedItems}
                      onToggleSelect={onToggleSelect}
                      expandedMap={expandedMap}
                      onToggleExpand={onToggleExpand}
                      headerDetail={headerDetail}
                      cut={cut}
                    />
                  ))
                ) : (
                  <Box sx={{ width: "100%", height: "100px", ...columnCenter }}>
                    No data found
                  </Box>
                )}

                {totalPages > 1 && (
                  <Box sx={{ ...rowCenter, width: "100%", mb: "10px" }}>
                    <Pagination
                      showFirstButton
                      showLastButton
                      defaultPage={currentPage}
                      page={currentPage}
                      count={totalPages}
                      onChange={(event: any, newPage: number) => {
                        setPagination((prev) => ({
                          ...prev,
                          [getNextPath(node.type)]: newPage,
                        }));
                      }}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );

  return isFullscreen ? (
    <Dialog
      open={true}
      sx={{
        "& .MuiDialog-paper": {
          margin: "0",
          minHeight: "100dvh",
          minWidth: "100%",
          height: "100dvh",
          borderRadius: 0,
        },
      }}
      fullScreen={true}
      TransitionProps={{ timeout: 400 }}
    >
      {child}
    </Dialog>
  ) : (
    <Box
      sx={
        isFullscreen
          ? {
              height: "100dvh",
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 9999,
              width: "100%",
              // background: "#FFFFFF",
              minWidth: "100%",
              transition: "all 0.4s ease-in-out",
            }
          : {
              mx: depth ? "8px" : "0",
              border: `1px solid #e5e7eb`,
              my: "10px",
              maxWidth: depth ? "calc(100% - 16px)" : "100%",
              boxSizing: "border-box",
              background: "#FFFFFF",
              transition: "all 0.4s ease-in-out",
            }
      }
      id={`parent-${node.type}-${node.id}`}
    >
      {child}
    </Box>
  );
}
